import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, TrendingDown, TrendingUp, CheckCircle } from 'lucide-react';
import type { Ingredient, Inventory } from '@/types/entities';
import { useValidateStock } from '@/hooks/useInventoryValidations';

interface StockValidationDialogProps {
  restaurantId: string;
  ingredient: Ingredient;
  currentInventory: Inventory;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function StockValidationDialog({
  restaurantId,
  ingredient,
  currentInventory,
  open,
  onOpenChange,
  onSuccess,
}: StockValidationDialogProps) {
  const { t, i18n } = useTranslation();
  const [actualQuantity, setActualQuantity] = useState('');
  const [reason, setReason] = useState('periodic_count');
  const [notes, setNotes] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  const { mutate: validateStock, isPending } = useValidateStock(restaurantId);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setActualQuantity('');
      setReason('periodic_count');
      setNotes('');
    }
  }, [open]);

  // Safety check - close dialog if ingredient is not available
  if (!ingredient) {
    return null;
  }

  const calculatedQty = currentInventory.quantity;
  const actualQty = parseFloat(actualQuantity) || 0;
  const difference = actualQty - calculatedQty;
  const costVariance = difference * currentInventory.averageCost;
  const percentageDiff = calculatedQty > 0 ? Math.abs((difference / calculatedQty) * 100) : 0;
  const isLargeDiscrepancy = percentageDiff > 20;

  const handleSubmit = () => {
    if (isLargeDiscrepancy && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    const qty = parseFloat(actualQuantity);
    if (isNaN(qty) || qty < 0) {
      return;
    }

    validateStock(
      {
        ingredientId: ingredient.id,
        actualQuantity: qty,
        reason,
        notes: notes || undefined,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          setShowConfirmation(false);
          onSuccess?.();
        },
      }
    );
  };

  const getDifferenceLabel = () => {
    if (difference === 0) {
      return {
        icon: CheckCircle,
        color: 'text-blue-600',
        bg: 'bg-blue-100',
        text: t('inventory.validateStockDialog.exactMatch'),
      };
    } else if (difference < 0) {
      return {
        icon: TrendingDown,
        color: 'text-red-600',
        bg: 'bg-red-100',
        text: t('inventory.validateStockDialog.shortage'),
      };
    } else {
      return {
        icon: TrendingUp,
        color: 'text-green-600',
        bg: 'bg-green-100',
        text: t('inventory.validateStockDialog.overage'),
      };
    }
  };

  const diffLabel = getDifferenceLabel();
  const DiffIcon = diffLabel.icon;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">{ingredient.emoji}</span>
              {t('inventory.validateStockDialog.title')} -{' '}
              {i18n.language === 'ar' ? ingredient.nameAr : ingredient.nameFr}
            </DialogTitle>
            <DialogDescription>{t('inventory.validateStockDialog.description')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0">
            {/* Calculated Quantity */}
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="text-sm text-muted-foreground mb-1">
                {t('inventory.validateStockDialog.calculatedQuantity')}
              </div>
              <div className="text-2xl font-bold">
                {calculatedQty.toFixed(3)} {currentInventory.unit}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {t('inventory.validateStockDialog.averageCost')}:{' '}
                {currentInventory.averageCost.toFixed(2)} {t('common.currency')}/
                {currentInventory.unit}
              </div>
            </div>

            {/* Actual Quantity Input */}
            <div className="space-y-2">
              <Label htmlFor="actual">{t('inventory.validateStockDialog.actualQuantity')} *</Label>
              <div className="flex gap-2">
                <Input
                  id="actual"
                  type="number"
                  step="0.001"
                  min="0"
                  value={actualQuantity}
                  onChange={(e) => setActualQuantity(e.target.value)}
                  placeholder="0.000"
                  required
                  className="flex-1"
                />
                <Badge variant="outline" className="px-3">
                  {currentInventory.unit}
                </Badge>
              </div>
            </div>

            {/* Show difference when actual quantity is entered */}
            {actualQuantity && (
              <div
                className={`rounded-lg border p-4 ${diffLabel.bg} border-${diffLabel.color.replace('text-', '')}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">
                    {t('inventory.validateStockDialog.difference')}:
                  </span>
                  <Badge className={`gap-1 ${diffLabel.bg} ${diffLabel.color}`}>
                    <DiffIcon className="h-3 w-3" />
                    {diffLabel.text}
                  </Badge>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t('inventory.validateStockDialog.quantity')}:
                    </span>
                    <span className="font-mono font-medium">
                      {difference > 0 ? '+' : ''}
                      {difference.toFixed(3)} {currentInventory.unit}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {t('inventory.validateStockDialog.costImpact')}:
                    </span>
                    <span
                      className={`font-mono font-medium ${costVariance < 0 ? 'text-red-600' : 'text-green-600'}`}
                    >
                      {costVariance > 0 ? '+' : ''}
                      {costVariance.toFixed(2)} {t('common.currency')}
                    </span>
                  </div>
                  {isLargeDiscrepancy && (
                    <div className="flex items-start gap-2 mt-2 text-amber-600">
                      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span className="text-xs">
                        {t('inventory.validateStockDialog.largeDiscrepancyWarning')} (
                        {percentageDiff.toFixed(1)}%)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">{t('inventory.validateStockDialog.reason')}</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="periodic_count">
                    {t('inventory.validateStockDialog.reasonPeriodic')}
                  </SelectItem>
                  <SelectItem value="discrepancy_check">
                    {t('inventory.validateStockDialog.reasonDiscrepancy')}
                  </SelectItem>
                  <SelectItem value="year_end">
                    {t('inventory.validateStockDialog.reasonYearEnd')}
                  </SelectItem>
                  <SelectItem value="other">
                    {t('inventory.validateStockDialog.reasonOther')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">{t('inventory.validateStockDialog.notes')}</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('inventory.validateStockDialog.notesPlaceholder')}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={isPending || !actualQuantity || parseFloat(actualQuantity) < 0}
            >
              {isPending
                ? t('inventory.validateStockDialog.validating')
                : t('inventory.validateStockDialog.confirmValidation')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Large Discrepancies */}
      <AlertDialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              {t('inventory.validateStockDialog.largeDiscrepancyTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>{t('inventory.validateStockDialog.largeDiscrepancyDescription')}</p>
              <div className="rounded-lg border bg-amber-50 p-3 text-sm space-y-1 text-right">
                <div>
                  <strong>{t('inventory.validateStockDialog.difference')}:</strong>{' '}
                  {difference.toFixed(3)} {currentInventory.unit} ({percentageDiff.toFixed(1)}%)
                </div>
                <div>
                  <strong>{t('inventory.validateStockDialog.costImpact')}:</strong>{' '}
                  {costVariance.toFixed(2)} {t('common.currency')}
                </div>
              </div>
              <p className="mt-2">{t('inventory.validateStockDialog.confirmQuestion')}</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit}>{t('common.confirm')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
